<template>
  <div class="three-d-map-page" v-loading="loading">
    <section class="map-panel">
      <div class="map-header">
        <div>
          <h2>高德 3D 360 环视</h2>
          <p>基于高德 JS API v2.0 的 3D 地图、旋转角和俯仰角实现地图级 360 度交互。</p>
        </div>
        <div class="map-status">
          <span>中心点：{{ currentCenterText }}</span>
          <span>旋转角：{{ currentRotation }}°</span>
          <span>俯仰角：{{ currentPitch }}°</span>
        </div>
      </div>

      <div ref="mapContainer" class="map-stage"></div>
    </section>

    <aside class="control-panel">
      <div class="control-card">
        <div class="control-title">环视控制</div>
        <div class="button-row">
          <el-button type="primary" size="small" @click="startAutoRotate" :disabled="autoRotating">
            开始环视
          </el-button>
          <el-button type="warning" size="small" @click="stopAutoRotate" :disabled="!autoRotating">
            暂停环视
          </el-button>
          <el-button size="small" @click="resetView">
            重置视角
          </el-button>
        </div>

        <div class="field-block">
          <div class="field-label">旋转角度</div>
          <el-slider
            v-model="selectedRotation"
            :min="0"
            :max="360"
            :step="1"
            show-input
            @change="applyRotation"
          />
        </div>

        <div class="field-block">
          <div class="field-label">俯仰角度</div>
          <el-slider
            v-model="selectedPitch"
            :min="0"
            :max="83"
            :step="1"
            show-input
            @change="applyPitch"
          />
        </div>

        <div class="field-block">
          <div class="field-label">地图缩放</div>
          <el-slider
            v-model="selectedZoom"
            :min="3"
            :max="20"
            :step="1"
            show-input
            @change="applyZoom"
          />
        </div>
      </div>

      <div class="control-card">
        <div class="control-title">场景切换</div>
        <el-select v-model="selectedSceneKey" size="small" style="width: 100%">
          <el-option
            v-for="scene in sceneOptions"
            :key="scene.key"
            :label="scene.label"
            :value="scene.key"
          />
        </el-select>
        <el-button type="primary" size="small" class="full-button" @click="applyScene">
          切换场景
        </el-button>
      </div>

      <div class="control-card">
        <div class="control-title">点击结果</div>
        <div class="result-item">
          <span>点击坐标</span>
          <strong>{{ clickCoordinateText }}</strong>
        </div>
        <div class="result-item">
          <span>当前缩放</span>
          <strong>{{ currentZoom }}</strong>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'

// 高德地图 Web JS API Key，用于加载 JS API v2.0。
const AMAP_KEY = '3178ec7da1e1519b695da09875bb59bc'
// 高德地图安全密钥，新版 Key 在 Web 端使用时需要提前写入全局安全配置。
const AMAP_SECURITY_CODE = 'c67fe061a0bdbbc0c91084b8aa572aa3'
// 地图初始化时使用的默认中心点，格式为 [经度, 纬度]。
const DEFAULT_CENTER = [116.333926, 39.997245]
// 地图初始化和重置时使用的默认缩放级别。
const DEFAULT_ZOOM = 17
// 地图初始化和重置时使用的默认俯仰角，3D 地图有效范围通常为 0-83。
const DEFAULT_PITCH = 50
// 地图初始化和重置时使用的默认旋转角，0 表示正北方向。
const DEFAULT_ROTATION = 0
// 自动环视时每一帧增加的旋转角度，数值越大转得越快。
const ROTATION_STEP = 0.2

export default {
  name: 'PcMap3D',

  data() {
    return {
      // 页面加载态，主要用于高德 SDK 加载和地图初始化阶段。
      loading: false,
      // 高德地图 SDK 命名空间对象，后续创建地图、控件和覆盖物都依赖它。
      AMap: null,
      // 当前页面的高德地图实例。
      map: null,
      // requestAnimationFrame 返回的动画任务 ID，用于停止自动环视。
      rotateAnimationId: null,
      // 是否正在自动 360 度环视。
      autoRotating: false,
      // 当前地图中心点坐标，格式为 [经度, 纬度]。
      currentCenter: [...DEFAULT_CENTER],
      // 当前地图缩放级别。
      currentZoom: DEFAULT_ZOOM,
      // 当前地图俯仰角。
      currentPitch: DEFAULT_PITCH,
      // 当前地图旋转角。
      currentRotation: DEFAULT_ROTATION,
      // 自动环视内部使用的精确旋转角，避免小数步进被界面展示的整数角度抹掉。
      animationRotation: DEFAULT_ROTATION,
      // 控制面板里选中的缩放级别。
      selectedZoom: DEFAULT_ZOOM,
      // 控制面板里选中的俯仰角。
      selectedPitch: DEFAULT_PITCH,
      // 控制面板里选中的旋转角。
      selectedRotation: DEFAULT_ROTATION,
      // 当前选中的示例场景 key。
      selectedSceneKey: 'zhongguancun',
      // 地图点击后展示的经纬度文本。
      clickCoordinateText: '-',
      // 可快速切换的 3D 地图中心点预设。
      sceneOptions: [
        { key: 'zhongguancun', label: '北京 · 中关村', center: [116.333926, 39.997245], zoom: 17 },
        { key: 'guomao', label: '北京 · 国贸', center: [116.461795, 39.91459], zoom: 17 },
        { key: 'shanghai', label: '上海 · 陆家嘴', center: [121.499809, 31.239666], zoom: 17 },
        { key: 'shenzhen', label: '深圳 · 市民中心', center: [114.057868, 22.543099], zoom: 17 }
      ]
    }
  },

  computed: {
    // 当前地图中心点的文本化展示。
    currentCenterText() {
      return this.formatLngLat(this.currentCenter)
    }
  },

  mounted() {
    // 组件挂载后初始化高德 3D 地图。
    this.initMap()
  },

  beforeDestroy() {
    // 组件销毁前停止动画并销毁地图实例，避免路由切换后仍占用浏览器资源。
    this.stopAutoRotate()
    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  },

  methods: {
    // 初始化高德地图 SDK、创建 3D 地图实例并挂载基础控件。
    async initMap() {
      this.loading = true
      try {
        window._AMapSecurityConfig = {
          securityJsCode: AMAP_SECURITY_CODE
        }

        const AMap = await AMapLoader.load({
          key: AMAP_KEY,
          version: '2.0',
          plugins: ['AMap.ControlBar', 'AMap.ToolBar', 'AMap.Scale']
        })

        this.AMap = AMap
        this.map = new AMap.Map(this.$refs.mapContainer, {
          viewMode: '3D',
          zoom: DEFAULT_ZOOM,
          center: DEFAULT_CENTER,
          pitch: DEFAULT_PITCH,
          rotation: DEFAULT_ROTATION,
          resizeEnable: true,
          rotateEnable: true,
          pitchEnable: true,
          zooms: [3, 20],
          mapStyle: 'amap://styles/normal'
        })

        this.addMapControls()
        this.bindMapEvents()
        this.syncMapState()
      } catch (error) {
        console.error('高德 3D 地图加载失败:', error)
        this.$toast.error('高德 3D 地图加载失败')
      } finally {
        this.loading = false
      }
    },

    // 添加 3D 控制条、缩放工具条和比例尺控件。
    addMapControls() {
      if (!this.map || !this.AMap) return
      this.map.addControl(new this.AMap.ControlBar({ position: { right: '16px', top: '16px' } }))
      this.map.addControl(new this.AMap.ToolBar({ position: { right: '44px', top: '126px' } }))
      this.map.addControl(new this.AMap.Scale())
    },

    // 绑定地图点击、拖拽、缩放和视角变化事件，用于同步页面状态。
    bindMapEvents() {
      if (!this.map) return
      this.map.on('click', this.handleMapClick)
      this.map.on('moveend', this.syncMapState)
      this.map.on('zoomend', this.syncMapState)
      this.map.on('rotatechange', this.syncMapState)
      this.map.on('pitchchange', this.syncMapState)
    },

    // 从地图实例读取中心点、缩放、旋转角和俯仰角，并同步到页面字段。
    syncMapState() {
      if (!this.map) return

      const center = this.map.getCenter()
      if (center) {
        this.currentCenter = [center.lng, center.lat]
      }

      this.currentZoom = this.map.getZoom()
      this.currentPitch = Math.round(this.map.getPitch())
      this.animationRotation = this.normalizeRotation(this.map.getRotation())
      this.currentRotation = Math.round(this.animationRotation)
      this.selectedZoom = this.currentZoom
      this.selectedPitch = this.currentPitch
      this.selectedRotation = this.currentRotation
    },

    // 开始自动 360 度环视，基于 requestAnimationFrame 持续更新地图旋转角。
    startAutoRotate() {
      if (!this.map || this.autoRotating) return
      this.animationRotation = this.normalizeRotation(this.map.getRotation())
      this.autoRotating = true
      this.rotateFrame()
    },

    // 停止自动 360 度环视，并清理当前动画任务。
    stopAutoRotate() {
      this.autoRotating = false
      if (this.rotateAnimationId) {
        cancelAnimationFrame(this.rotateAnimationId)
        this.rotateAnimationId = null
      }
    },

    // 自动环视的单帧逻辑：累加旋转角并申请下一帧动画。
    rotateFrame() {
      if (!this.map || !this.autoRotating) return
      const nextRotation = this.normalizeRotation(this.animationRotation + ROTATION_STEP)
      this.map.setRotation(nextRotation)
      this.animationRotation = nextRotation
      this.currentRotation = Math.round(nextRotation)
      this.selectedRotation = this.currentRotation
      this.rotateAnimationId = requestAnimationFrame(this.rotateFrame)
    },

    // 应用控制面板中的旋转角到地图。
    applyRotation() {
      if (!this.map) return
      this.stopAutoRotate()
      this.map.setRotation(this.selectedRotation)
      this.syncMapState()
    },

    // 应用控制面板中的俯仰角到地图。
    applyPitch() {
      if (!this.map) return
      this.map.setPitch(this.selectedPitch)
      this.syncMapState()
    },

    // 应用控制面板中的缩放级别到地图。
    applyZoom() {
      if (!this.map) return
      this.map.setZoom(this.selectedZoom)
      this.syncMapState()
    },

    // 根据当前选择的场景预设切换地图中心点和缩放级别。
    applyScene() {
      if (!this.map) return
      const scene = this.sceneOptions.find(item => item.key === this.selectedSceneKey)
      if (!scene) return

      this.map.setZoomAndCenter(scene.zoom, scene.center)
      this.currentCenter = [...scene.center]
      this.selectedZoom = scene.zoom
      this.syncMapState()
    },

    // 将地图视角恢复到默认中心点、缩放、旋转角和俯仰角。
    resetView() {
      if (!this.map) return
      this.stopAutoRotate()
      this.map.setZoomAndCenter(DEFAULT_ZOOM, DEFAULT_CENTER)
      this.map.setPitch(DEFAULT_PITCH)
      this.map.setRotation(DEFAULT_ROTATION)
      this.syncMapState()
    },

    // 地图点击事件：读取点击位置的经纬度并展示在右侧面板。
    handleMapClick(event) {
      if (!event || !event.lnglat) return
      this.clickCoordinateText = this.formatLngLat([event.lnglat.lng, event.lnglat.lat])
    },

    // 把任意旋转角归一化到 0-360 范围内。
    normalizeRotation(rotation) {
      return ((Number(rotation) % 360) + 360) % 360
    },

    // 把经纬度数组格式化成固定 6 位小数的展示文本。
    formatLngLat(position) {
      if (!Array.isArray(position) || position.length < 2) return '-'
      const lng = Number(position[0])
      const lat = Number(position[1])
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return '-'
      return `${lng.toFixed(6)}, ${lat.toFixed(6)}`
    }
  }
}
</script>

<style scoped lang="scss">
.three-d-map-page {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 18px;
  /* 撑满内容区，不再手算 calc(100vh - Npx) */
  height: 100%;
  box-sizing: border-box;
  padding: 0;
  background: transparent;
}

.map-panel,
.control-card {
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  background: var(--surface-1);
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.08);
}

.map-panel {
  display: flex;
  min-width: 0;
  min-height: 680px;
  overflow: hidden;
  flex-direction: column;
}

.map-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  background: var(--surface-1);
}

.map-header h2 {
  margin: 0 0 8px;
  color: #1f2d3d;
  font-size: 20px;
}

.map-header p {
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.6;
}

.map-status {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 260px;
}

.map-status span {
  padding: 7px 10px;
  border-radius: 6px;
  background: #ecf5ff;
  color: #315dcf;
  font-size: 12px;
  white-space: nowrap;
}

.map-stage {
  width: 100%;
  flex: 1;
  min-height: 600px;
}

.control-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.control-card {
  padding: 18px;
}

.control-title {
  margin-bottom: 14px;
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 600;
}

.button-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.field-block {
  margin-top: 18px;
}

.field-label {
  margin-bottom: 10px;
  color: #42536a;
  font-size: 14px;
  font-weight: 500;
}

.full-button {
  width: 100%;
  margin-top: 12px;
}

.result-item {
  padding: 12px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  background: #f7fbff;
}

.result-item + .result-item {
  margin-top: 10px;
}

.result-item span {
  display: block;
  margin-bottom: 6px;
  color: #7a8797;
  font-size: 12px;
}

.result-item strong {
  color: #1f2d3d;
  font-size: 14px;
  word-break: break-all;
}

@media (max-width: 1100px) {
  .three-d-map-page {
    grid-template-columns: 1fr;
  }

  .map-header {
    flex-direction: column;
  }

  .map-status {
    justify-content: flex-start;
    min-width: 0;
  }
}
</style>
